import {
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "../../utils";
import { Button } from "./button";
import { SectionCard } from "./SectionCard";

export interface FilterPanelProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  resetLabel?: string;
  onReset?: () => void;
  resetDisabled?: boolean;
  actions?: ReactNode;
  gridClassName?: string;
}

export function FilterPanel({
  title,
  description,
  resetLabel,
  onReset,
  resetDisabled = false,
  actions,
  gridClassName,
  className,
  children,
  ...props
}: FilterPanelProps) {
  const resetAction =
    resetLabel && onReset ? (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onReset}
        disabled={resetDisabled}
      >
        {resetLabel}
      </Button>
    ) : null;

  return (
    <SectionCard
      title={title}
      description={description}
      actions={
        actions || resetAction ? (
          <>
            {actions}
            {resetAction}
          </>
        ) : undefined
      }
      bodyClassName={cn("pt-4", gridClassName)}
      className={className}
      density="compact"
      elevation="surface"
      {...props}
    >
      {children}
    </SectionCard>
  );
}

export interface FilterFieldProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  htmlFor?: string;
}

export function FilterField({
  label,
  htmlFor,
  className,
  children,
  ...props
}: FilterFieldProps) {
  const generatedControlId = useId();
  const childControlId = isValidElement(children)
    ? (children.props as { id?: string }).id
    : undefined;
  const controlId = htmlFor ?? childControlId ?? generatedControlId;
  const labelledControl = isValidElement(children) ? (
    cloneElement(children as ReactElement<{ id?: string }>, {
      id: controlId,
      key: "control",
    })
  ) : (
    <span key="control">{children}</span>
  );

  return (
    <div className={cn("space-y-1.5", className)} {...props}>
      <label
        key="label"
        htmlFor={controlId}
        className="block text-caption font-semibold uppercase tracking-[0.12em] text-content-muted"
      >
        {label}
      </label>
      {labelledControl}
    </div>
  );
}
